import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {MatExpansionModule} from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NewGameService } from '../../../services/games/new-game';
import { PlayersService } from '../../../services/players/players';
import { exhaustMap, tap } from 'rxjs';
import { MatRadioModule } from '@angular/material/radio';
import { Downtime } from '../../../../core/services/times/downtime';
import { ListQuest } from '../../../services/lists/list-quest';
import { NotificationService } from '../../../../core/services/Notification/notification';
import { AllStatus } from '../../../../core/services/status/all-status';
import { StorageService } from '../../../../core/services/storage/storage.service';
import { StorageAppKey } from '../../../../core/services/storage/enumes/storage-app-key';

interface INGame{
  name: string,
  password: string,
  password_required: boolean,
  maximum_players: number,
  type: string
}

interface ITypeGame{
  slug: string,
  name: string
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

interface StoredUser {
  id: string;
  is_demo: boolean;
  access_token: string;
  expires: number;
  expire_at: number;
  refresh_token: string;
}

function isStoredUser(value: unknown): value is StoredUser {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const user = value as Record<string, unknown>;
  return typeof user['id'] === 'string'
    && typeof user['is_demo'] === 'boolean'
    && typeof user['access_token'] === 'string'
    && typeof user['expires'] === 'number'
    && typeof user['expire_at'] === 'number'
    && typeof user['refresh_token'] === 'string';
}

@Component({
  selector: 'app-new-game',
  imports: [TranslatePipe,
    MatFormFieldModule, 
    MatInputModule, 
    MatButtonModule, 
    MatIconModule,
    MatRadioModule, 
    MatCardModule,
    MatExpansionModule,
    MatProgressBarModule, 
    ReactiveFormsModule],
  templateUrl: './new-game.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './new-game.scss'
})
export class NewGame implements OnInit {
  
  
  templ = input<string>();
  errorMessage: string = '';
  quests = inject(ListQuest);
  uuid: string = '';
  isCreate: boolean = false;
  router = inject(Router);
  createNG = inject(NewGameService);
  players = inject(PlayersService);
  downtime = inject(Downtime);
  notification = inject(NotificationService);

  allStatus$ = inject(AllStatus);
  translate = inject(TranslateService);
  txtDialog: any;
  isCreateClick: boolean = false;
  storage = inject(StorageService);

  typeGames: Array<ITypeGame> = [{
    slug: 'quest',
    name: "txtQuest"
  },{
    slug: "role",
    name: "txtRole"
  }];

  countArraySelect: Array<number> = [2,3,4,5,6];

  createForm = new FormGroup({
    gameTitle: new FormControl('',[
      Validators.required
    ]),
    code: new FormControl(''),
    countPlayer: new FormControl(1, [
      Validators.required,
      Validators.min(1),
      Validators.max(6)
    ]),
    selectTypeGame: new FormControl('quest', Validators.required),
  });
  subscriptionCL: any;

  constructor( private ref: ChangeDetectorRef){}

  ngOnInit(): void {
    this.isCreateClick = false;
    this.loadTranslateText();
    this.isCreate = false;
    if(this.templ() === "many-players"){
      this.createForm.get("countPlayer")?.setValue(2);
    }
    if(this.createForm.get("gameTitle")?.value === ''){
      
      this.createForm.get('gameTitle')?.setErrors({'required': true});
      this.createForm.get('gameTitle')?.markAsTouched();
      this.ref.markForCheck();
      
    } 
    this.subscriptionCL = this.allStatus$.changeLanguage$.subscribe((change: boolean)=>{
      if(change){
        this.allStatus$.setChangeLanguage(false);
        this.loadTranslateText();
      }
    });   
  }

  loadTranslateText(){
    this.translate.get(['spinner']).subscribe(translations => {
      this.txtDialog = translations['spinner'];
    });
  }

  changeCount(value: number):void{
    this.createForm.get("countPlayer")?.setValue(value);
  }

  blurInput():void{
    
    if(this.createForm.get('gameTitle')?.value?.length === 0){
      this.createForm.get('gameTitle')?.setErrors({'required': true});
      this.ref.markForCheck(); 
      
    } 
  }

  submitForm(){
    const user = this.storage.read<StoredUser>(StorageAppKey.User, isStoredUser);
    this.isCreateClick = false;
    this.allStatus$.textSpinner = this.txtDialog.txtCreating;
    this.allStatus$.setDisplayProgressSpinner(true);
    this.errorMessage = '';
    this.quests.clearStorage();
    sessionStorage.setItem("typeGame", JSON.stringify(this.createForm.value.selectTypeGame as string));
    sessionStorage.setItem("gamePeople", JSON.stringify('many'));
    this.isCreate = true;
    const data: INGame = {
      name: this.createForm.value.gameTitle as string,
      password: this.createForm.value.code as string,
      password_required: (this.createForm.value.code) ? true : false,
      maximum_players: this.createForm.value.countPlayer as number,
      type: this.createForm.value.selectTypeGame as string
    }

    this.downtime.refreshTimer();

    this.createNG.createNewGame(data).pipe(
      tap((apartment: any) => this.uuid = apartment.room.uuid),
      exhaustMap((apartment: any)=>  this.players.createPlayerInRoom(apartment.room, true, this.createForm.value.code as string)),
      
    ).subscribe({
      next: (result: any)=> {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'game_create',
          userId: user?.id,
          name: data.name
        });
        this.players.player = result.room;
        this.router.navigate([`/waiting/${this.uuid}`])
      },
      error: (err: any) => {
        this.allStatus$.textSpinner = '';
        this.allStatus$.setDisplayProgressSpinner(false);
        console.error(err.error.message);
        this.errorMessage = err.error.message;
        this.createForm.get('gameTitle')?.setErrors({'invalid': true});
        this.ref.markForCheck(); 
        this.notification.setMessage(err.error.message); 
        this.notification.display();
      }
    });
  }

  ExitCreateRoom(){
    this.downtime.refreshTimer();
    this.router.navigate(['/step-two']);
  }
}
